/**
 * Work samples (#603): getting a picked file ready to upload. Photos are shrunk to at most 2,000 pixels on the long
 * side and saved as JPEG (that keeps them small and in a format the portfolio PDF can hold, whatever the camera
 * made); PDFs go as they are. Anything else is refused.
 */
export interface SampleUpload {
  name: string;
  data: string; // base64, no data: prefix
  size: number;
}

const MAX_SIDE = 2000;

const toBase64 = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });

async function photoAsJpeg(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file).catch(() => null);
  let w: number;
  let h: number;
  let draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void;
  if (bitmap) {
    w = bitmap.width;
    h = bitmap.height;
    draw = (ctx, cw, ch) => ctx.drawImage(bitmap, 0, 0, cw, ch);
  } else {
    // Older browsers: through an <img>.
    const url = URL.createObjectURL(file);
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('unreadable'));
      i.src = url;
    }).finally(() => setTimeout(() => URL.revokeObjectURL(url), 1000));
    w = img.naturalWidth;
    h = img.naturalHeight;
    draw = (ctx, cw, ch) => ctx.drawImage(img, 0, 0, cw, ch);
  }
  const k = Math.min(1, MAX_SIDE / Math.max(w, h));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(w * k);
  canvas.height = Math.round(h * k);
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#ffffff'; // transparent PNGs get a white page, not black
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  draw(ctx, canvas.width, canvas.height);
  bitmap?.close();
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('unreadable'))), 'image/jpeg', 0.85));
}

/** Throws an Error with a message for the person when the file can't be used. */
export async function prepareSample(file: File, maxMb: number): Promise<SampleUpload> {
  const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
  if (isPdf) {
    if (file.size > maxMb * 1024 * 1024) throw new Error(`${file.name} is bigger than ${maxMb} MB.`);
    return { name: file.name, data: await toBase64(file), size: file.size };
  }
  if (!file.type.startsWith('image/') && !/\.(jpe?g|png|gif|webp|heic|heif|bmp)$/i.test(file.name)) throw new Error(`${file.name} isn’t a photo or a PDF.`);
  let jpeg: Blob;
  try {
    jpeg = await photoAsJpeg(file);
  } catch {
    throw new Error(`${file.name} couldn’t be opened. Try a JPEG or PNG.`);
  }
  if (jpeg.size > maxMb * 1024 * 1024) throw new Error(`${file.name} is bigger than ${maxMb} MB even after shrinking it.`);
  return { name: file.name.replace(/\.[^.]+$/, '') + '.jpg', data: await toBase64(jpeg), size: jpeg.size };
}

export const fileSize = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
